import {
	find,
	filter,
	some,
	xorWith
} from "lodash";
import template from "./multi-select.html";
import "./multi-select.less";
import {
	EVENT_REPORT_LOCKED,
	EVENT_FILTER_CURRENT_OPTIONS,
	EVENT_FILTER_ALL_OPTIONS,
	EVENT_VALUE_CHANGED,
	EVENT_CLEAR_VALUE,
	FEATURE_TYPES,
	EVENT_SET_FEATURE_DATA,
	EVENT_REVERT_VALUES,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";

// multiSelect
export class Controller {
	options = [];
	selections = [];

	constructor(uiHelpers, eventBus, $scope, $logger) {
		this.uiHelpers = uiHelpers;
		this._scope = $scope;
		this.search = true;
		this.$logger = $logger;
		this.runOnReady = [];
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		const ctrl = this;
		this.setOptionIdType();

		this.setFeatures();

		this._eventBus.register(EVENT_REPORT_LOCKED, () => this.box.disable(), this._scope);
		this._eventBus.register(EVENT_FILTER_CURRENT_OPTIONS(this.field.Id), (e, params) => this.setOptions(params.ids), this._scope);
		this._eventBus.register(EVENT_FILTER_ALL_OPTIONS(this.field.Id), (e, params) => {
			this.setOptions(params.ids);
		}, this._scope);
		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this.options = [...this.field.Data];

		this.initialValues = filter(this.values, v => v.FieldId === this.field.Id);
		this.setValue();

		this._unregister = this.reportFormCtrl.registerFieldController(this);

		if(this.selections.length) {
			const val = filter(this.field.Data, d => some(ctrl.selections.map(s => s.Id), (v) => d.Id == v));
			val.map(v => ctrl.options.push(v));
		}

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), () => {
			const clearValue = () => {
				if(this.field.isReadonly) {
					this.box.disable();
				}

				this.selections = [];
				this.box.removeActiveItems();
			};

			if(this.isReady) {
				clearValue();
			}
			else {
				this.runOnReady.push(clearValue);
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	setFeatures() {
		const mapPointFeature = find(this.field.features, { Type: FEATURE_TYPES.mapPoint });

		if(mapPointFeature) {
			this._eventBus.register(EVENT_SET_FEATURE_DATA(mapPointFeature.Type), (e, params) => {
				this.selections = filter(this.field.Data, o => some(params.areaIds, v => o.Id === v));
			}, this._scope);
		}
	}

	getCurrentValue(isApiCall) {
		const result = this.buildResult(isApiCall);

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			const unmatched = xorWith(result, this.initialValues, (a, b) => {
				return a.FieldId === b.FieldId && a[this.optionIdType] === b[this.optionIdType];
			});

			if(unmatched.length > 0) {
				result.forEach(r => r.isDirty = true);
			}
		}

		return result;
	}

	revertValue() {
		this.setChoices();
		this.value = this.initialValues.map(v => v[this.optionIdType].toString());
		this.handleValueChanged();
		this.setChoice();
	}

	setPristine() {
		this.initialValues = this.buildResult();
	}

	buildResult(isApiCall = false) {
		return this.selections.length
			? this.selections.map(x => {
				const result = {
					FieldId: this.field.Id,
					[this.optionIdType]: x.Id
				};

				if(!isApiCall) {
					result.Name = x.Name;
					result._code = x.Code;
					result.ExternalKey = x.ExternalKey;
					result.DeactivatedAt = x.DeactivatedAt;
					result.Shape = x.Shape;
					result.Geometries = x.Geometries ?? [];
				}

				return result;
			})
			: [];
	}

	setValue() {
		const values = filter(this.values, v => v.FieldId === this.field.Id);

		if(values.length) {
			this.selections = filter(this.field.Data, d => some(values, v => d.Id === v[this.optionIdType]));
		}
	}

	setSelectBox(box) {
		this.box = box;
		this.setChoices();
		this.setChoice();

		if(this.selections) {
			this.handleValueChanged();
		}

		this.isReady = true;
		this.runOnReady.forEach(func => func());
		this.runOnReady = [];
	}

	setChoices(data) {
		this.options = data || this.field.Data;

		if(!this.box)
			return;

		const selectionChoices = this.uiHelpers.setDisplay(
			this.options,
			{
				recentlyUsedEnabled: this.groupRecents,
				hideCode: this.field.IsCodeHidden
			}
		);

		this.box.setChoices(selectionChoices, "value", "label", true);
	}

	setChoice() {
		if(this.selections.length) {
			this.box.setValueByChoice(this.selections.map(v => v.Id.toString()));
		}
	}

	setDisplay(id) {
		const item = find(this.field.Data, { Id: parseInt(id) });

		if(!item)
			throw new Error("Selected item not found.");

		return item.Name;
	}

	setOptions(ids) {
		this.options = filter(this.field.Data, (d) => some(ids, (i) => d.Id === i));

		if(!(ids || []).length && !(this.selections || []).length)
			this.options = this.field.Data;

		this.setChoices(this.options);
	}

	setOptionIdType() {
		switch(this.field.Type.Code) {
			case "ENTITYCONTACT":
			case "ENTITY":
				this.optionIdType = "EntityId";
				break;

			default: //AREA, PORT, REEF, SPECIES,
				this.optionIdType = "DataId";
				break;
		}
	}

	handleValueChanged() {
		if(this.value) {
			this.selections = filter(this.field.Data, o => some(this.value, v => o.Id === parseInt(v)));
		}

		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), {
			field: this.field,
			value: this.selections.map(s => s.Id)
		});

		const currentVal = this.getCurrentValue();

		const isDirty = (this.field.visible !== false || this.initialValue !== undefined) && some(currentVal, v => v.isDirty);

		this.setFieldDirty(this.field.Id, isDirty);
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	getItemNames(separator = ", ") {
		return this.selections.reduce((names, item, index) => {
			return `${names}${index > 0 ? separator : ""}${item.Name}`;
		}, "");
	}

	getTooltipItemNames() {
		return this.getItemNames("\n");
	}
}

export default angular
	.module("vesl.components.report-form.multi-select", [])
	.component("multiSelect", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
